import { useMemo } from 'react';
import {
    DistinctFacet,
    DistinctFacetItem,
} from '~/lib/data-contract/auto-generated/loop54-api-model';
import { useFrameNavigation, useTranslations } from '~/services/umbraco/hooks';
import { useUrlQueryState } from '~/shared/hooks';
import { FilteredNodeProps } from '../Navigation/Desktop/NavigationFilter';
import { getTranslatedFacetName } from '../helpers/filterHelper';
import { INavigationNode } from '~/lib/data-contract/umbraco-api-model';
import gtm from '~/shared/tracking/gtm';
import { NavigationFilterDesktop } from '../Navigation/Desktop';
import { NavigationFilterMobile } from '../Navigation/Mobile';

type FilterType = 'mobile' | 'desktop';
type Props = {
    searchNavigationItems: DistinctFacet[] | undefined;
    filterType: FilterType;
};

const NavigationFilter = ({ searchNavigationItems, filterType }: Props) => {
    const { translate } = useTranslations();
    const [selectedFilters, setSelectedFilters, clearFilter] = useUrlQueryState();
    const { data: navigation } = useFrameNavigation((data) => data?.mainMenu);

    const viewAll = translate('generic.viewAll');

    const navigationItems = useMemo(
        () => searchNavigationItems?.filter((facet) => facet.name === 'CategoryPath')[0],
        [searchNavigationItems],
    );
    const navigationTree = useMemo(() => {
        const navigationFacet = searchNavigationItems?.filter(
            (item) => item.name === 'CategoryPath',
        )[0]?.items;

        return navigationFacet && navigation
            ? filterType === 'desktop'
                ? GetFilteredNavigationItems(navigation, navigationFacet, viewAll)
                : GetFilteredNavigationItems(
                      navigation,
                      navigationFacet,
                      viewAll,
                      0,
                      true,
                      selectedFilters['CategoryPath'],
                  )
            : ([] as FilteredNodeProps[]);
    }, [filterType, navigation, searchNavigationItems, selectedFilters, viewAll]);

    const facetDisplayName = getTranslatedFacetName(navigationItems?.name, translate);

    const onSelectedFilterHandler = (
        selected: DistinctFacetItem,
        facetName?: string,
        facetDisplayName?: string,
    ) => {
        const label = selected?.item?.toString() ?? '';

        if (!selected || !label) {
            clearFilter();
        } else {
            if (facetName) {
                if ((selectedFilters[facetName]?.length ?? 0) < label.length && facetDisplayName) {
                    gtm.custom.filters(facetDisplayName);
                }

                setSelectedFilters(facetName, [label]);
            }
        }
    };

    if (!navigationItems) return null;

    return filterType === 'desktop' ? (
        <NavigationFilterDesktop
            navigationTree={navigationTree}
            onSelectedFilterHandler={onSelectedFilterHandler}
            facetDisplayName={facetDisplayName}
            navigationName={navigationItems.name}
            viewAll={viewAll}
            hasSelectedCategory={!!selectedFilters['CategoryPath']?.length}
        />
    ) : (
        <NavigationFilterMobile
            navigationTree={navigationTree}
            onSelectedFilterHandler={onSelectedFilterHandler}
            facetDisplayName={facetDisplayName}
            navigationName={navigationItems.name}
            hasSelectedCategory={!!selectedFilters['CategoryPath']?.length}
        />
    );
};

export default NavigationFilter;

export function GetFilteredNavigationItems(
    existingNavigation: INavigationNode[],
    navigationFacets: DistinctFacetItem[],
    viewAll: string,
    linkLevel: number = 0,
    onlyOneTier?: boolean,
    selectedFilters?: string[],
): FilteredNodeProps[] {
    const nav = existingNavigation
        .map((item) => {
            const filteredChildren = item.children
                ? GetFilteredNavigationItems(
                      item.children,
                      navigationFacets,
                      viewAll,
                      linkLevel + 1,
                  )
                : [];
            const matchingFacet = navigationFacets.find((facet) => facet.item === item.link.text);

            if (!matchingFacet) return null;

            return {
                ...item,
                children: filteredChildren,
                filterValue: matchingFacet,
                linkLevel: linkLevel,
            };
        })
        .filter((item) => item !== null) as FilteredNodeProps[];

    if (!!onlyOneTier && Boolean(selectedFilters?.length))
        return FindVisibleItems(nav, viewAll, selectedFilters);

    return nav;
}

function FindVisibleItems(
    filteredNodes: FilteredNodeProps[],
    viewAll: string,
    selectedFilters?: string[],
    parent?: FilteredNodeProps,
) {
    const result: FilteredNodeProps[] = [];

    if (!selectedFilters) return filteredNodes;

    for (const item of filteredNodes) {
        const matchingValue = selectedFilters.find((valueToKeep) => valueToKeep === item.link.text);

        if (matchingValue) {
            if (!!parent) {
                result.push({
                    ...parent,
                    link: { text: viewAll + ' ' + parent.link.text, url: parent.link.url },
                });
            } else {
                result.push({ link: { text: viewAll, url: '' } });
            }

            if (item.children && (item.children?.length || 0) > 0) {
                result.push({ ...item });
                result.push(...item.children.map((child) => ({ ...child })));
            } else {
                result.push(...filteredNodes.map((child) => ({ ...child })));
            }
        } else if (item.children && (item.children?.length || 0) > 0) {
            const childMatches = FindVisibleItems(item.children, viewAll, selectedFilters, item);

            if (childMatches.length > 0) {
                result.push(...childMatches);
            }
        }
    }

    return result;
}
